import { Component, OnInit, signal, computed } from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { finalize } from "rxjs/operators";
import { SearchBoxComponent } from "../../../shared/components/search-box/search-box.component";
import { SpinnerLoaderComponent } from "../../../shared/components/spinner-loader/spinner-loader.component";
import { UserDetailsModalComponent } from "./user-details-modal/user-details-modal.component";
import { ConfirmService } from "../../../Utils/_services/confirmation-dialog.service";
import { IUser } from "../../../Utils/_models/users.model";
import { IPagedRequest } from "../../../Utils/_models/request.model";
import { AdminService } from "../../../Utils/_services/admin/admin/admin.service";
import { ToastrService } from "ngx-toastr";
import { UserStatusModalComponent, StatusActionPayload } from "./user-status-modal/user-status-modal.component";
type UserStatus = "Active" | "Inactive" | "Suspended";

@Component({
  selector: "app-users",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePipe,
    SearchBoxComponent,
    SpinnerLoaderComponent,
    UserDetailsModalComponent,
    UserStatusModalComponent,
  ],
  templateUrl: "./users.component.html",
  styleUrls: ["./users.component.css"],
})
export class UsersComponent implements OnInit {
  constructor(
    private adminService: AdminService,
    private confirmationService: ConfirmService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService
  ) { }
  previewPhotos = signal<any[]>([]);
  previewPhotoIndex = signal<number>(0);
  previewPopupUrl = computed(() => {
    const photos = this.previewPhotos();
    if (photos.length === 0) return null;
    const idx = this.previewPhotoIndex();
    const safeIdx = idx >= 0 && idx < photos.length ? idx : 0;
    return photos[safeIdx]?.signedUrl || null;
  });

  searchTerm = signal<string | null>(null);
  currentPage = signal(1);
  totalCount = signal(0);
  users = signal<IUser[]>([]);
  originalUsers = signal<IUser[]>([]);
  allSelected = signal(false);
  loading = signal(false);
  pageSizes: number[] = [5, 10, 20, 50];
  pageSize = signal(0);
  totalPages: number = 0;
  startIndex: number = 1;
  endIndex: number = 10;
  isView = signal(false);
  selectedUser = signal<IUser | null>(null);

  isStatusModalVisible = signal(false);
  selectedUserForStatus = signal<IUser | null>(null);


  currentSortField = signal<string | null>(null);
  currentSortOrder = signal<"asc" | "desc" | null>(null);
  includeDeleted = signal(false);

  hasSelection = signal(false);
  hasDeletedSelection = signal(false);

  ngOnInit(): void {
    this.pageSize.set(this.pageSizes[0]);
    this.route.queryParams.subscribe(params => {
      if (params['search']) {
        this.onSearch(params['search']);
        this.searchTerm.set(params['search']);
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { search: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
      this.fetchUsers();
    });
  }

  toggleSelectAll(): void {
    this.allSelected.update(v => !v);
    this.users().forEach(u => {
      u.selected = this.allSelected();
    });
    this.updateSelectAllState();
  }

  onSort(field: string): void {
    if (this.currentSortField() === field) {
      if (this.currentSortOrder() === "asc") {
        this.currentSortOrder.set("desc");
      } else if (this.currentSortOrder() === "desc") {
        this.currentSortOrder.set(null);
        this.currentSortField.set(null);
      }
    } else {
      this.currentSortField.set(field);
      this.currentSortOrder.set("asc");
    }
    this.sortUsersLocal();
  }

  sortUsersLocal(): void {
    const field = this.currentSortField();
    const order = this.currentSortOrder();

    if (!field || !order) {
      this.users.set([...this.originalUsers()]);
      this.updatePagination();
      return;
    }

    const direction = order === "asc" ? 1 : -1;
    const sorted = [...this.users()];

    sorted.sort((a: any, b: any) => {
      let valA = a[field];
      let valB = b[field];

      if (field === 'firstName') {
        const nameA = ((a.firstName || '') + ' ' + (a.lastName || '')).toLowerCase();
        const nameB = ((b.firstName || '') + ' ' + (b.lastName || '')).toLowerCase();
        valA = nameA;
        valB = nameB;
      } else if (field === 'email') {
        valA = (a.email || '').toLowerCase();
        valB = (b.email || '').toLowerCase();
      } else if (field === 'phoneNumber') {
        valA = (a.phoneNumber || '').toLowerCase();
        valB = (b.phoneNumber || '').toLowerCase();
      } else if (field === 'lastLoginAt') {
        valA = a.lastLoginAt ? new Date(a.lastLoginAt).getTime() : 0;
        valB = b.lastLoginAt ? new Date(b.lastLoginAt).getTime() : 0;
      } else if (field === 'status') {
        valA = this.statusOf(a).toLowerCase();
        valB = this.statusOf(b).toLowerCase();
      } else if (field === 'totalLobbiesJoined') {
        valA = Number(a.totalLobbiesJoined || 0);
        valB = Number(b.totalLobbiesJoined || 0);
      } else if (field === 'totalMatches') {
        valA = Number(a.totalMatches || 0);
        valB = Number(b.totalMatches || 0);
      }

      if (valA < valB) return -1 * direction;
      if (valA > valB) return 1 * direction;
      return 0;
    });

    this.users.set(sorted);
    this.updatePagination();
  }

  getSortIcon(field: string): string {
    if (this.currentSortField() !== field) return "⇅";
    if (this.currentSortOrder() === "asc") return "↑";
    if (this.currentSortOrder() === "desc") return "↓";
    return "⇅";
  }

  fetchUsers(): void {
    this.allSelected.set(false);
    this.loading.set(true);

    const req: IPagedRequest = {
      page: this.currentPage(),
      limit: this.pageSize(),
      search: this.searchTerm(),
      sort: this.currentSortField(),
      sortOrder: this.currentSortField() ? this.currentSortOrder() : null,
      includeDeleted: this.includeDeleted()
    };

    this.adminService
      .getAllUsers(req)
      .pipe(
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: res => {
          const apiUsers = res.data?.users ?? [];
          this.users.set(apiUsers);
          this.originalUsers.set([...apiUsers]);
          this.totalCount.set(res.data.totalCount);
          if (this.currentSortField() && this.currentSortOrder()) {
            this.sortUsersLocal();
          } else {
            this.updatePagination();
          }
          this.updateSelectAllState();
        },
        error: () => {
          this.users.set([]);
          this.totalCount.set(0);
        },
      });
  }

  onPageSizeChange(newSize: number) {
    this.pageSize.set(newSize);
    this.currentPage.set(1);
    this.fetchUsers();
  }

  statusOf(u: IUser): string {
    if (u.deletedAt) return "Deleted";
    return u.isActive ? "Active" : "Inactive";
  }

  trackById(index: number, item: IUser): string | number {
    return item._id || index;
  }

  openStatusModal(event: Event, user: IUser) {
    event.preventDefault(); // Stop the checkbox from toggling immediately
    this.selectedUserForStatus.set(user);
    this.isStatusModalVisible.set(true);
  }

  closeStatusModal() {
    this.selectedUserForStatus.set(null);
    this.isStatusModalVisible.set(false);
  }

  handleStatusAction(payload: StatusActionPayload) {
    const suspendedUntilStr = payload.suspendDays ? new Date(Date.now() + payload.suspendDays * 24 * 60 * 60 * 1000).toISOString() : undefined;

    const mappedAccountStatus = payload.action === 'ban' ? 'banned' : (payload.action === 'suspend' ? 'suspended' : 'active');

    this.adminService.updateUserStatus(
      payload.user._id,
      payload.action === 'active' || payload.action === 'suspend',
      mappedAccountStatus,
      suspendedUntilStr,
      payload.reason,
      payload.sendEmail,
      payload.emailSubject,
      payload.emailBody,
      payload.suspendedServices
    ).subscribe({
      next: res => {
        if (res.auth.success) {
          const user = this.users().find(u => u._id === payload.user._id);
          if (user) {
            user.isActive = payload.action === 'active';
            user.accountStatus = payload.action === 'ban' ? 'banned' : (payload.action === 'suspend' ? 'suspended' : 'active');
            user.reason = payload.reason;
            user.suspendedServices = payload.suspendedServices;
            user.suspendedUntil = suspendedUntilStr ? new Date(suspendedUntilStr) : undefined;
          }
          this.toastr.success(`User status updated successfully`);
          this.closeStatusModal();
        } else {
          this.toastr.error('Failed to update user status');
        }
      },
      error: (err) => {
        this.toastr.error(err?.error?.message || 'Failed to update user status');
      }
    });
  }

  toggleIncludeDeleted() {
    this.includeDeleted.update(v => !v);
    this.currentPage.set(1);
    this.fetchUsers();
  }

  async deleteSelectedUsers() {
    const selectedUsers = this.users().filter(u => u.selected);
    const selectedIds = selectedUsers.map(u => u._id);
    if (selectedIds.length === 0) return;

    const deletedSelected = selectedUsers.filter(u => u.deletedAt);
    const activeSelected = selectedUsers.filter(u => !u.deletedAt);
    const isRestore = deletedSelected.length > 0;

    const header = isRestore ? 'Restore Users' : 'Delete Users';
    const bodyHeader = 'Are you sure?';
    const bodySubtitle = isRestore
      ? `You are about to restore ${deletedSelected.length} deleted user(s).${activeSelected.length > 0 ? ` ${activeSelected.length} non-deleted user(s) will also be deleted & deactivated.` : ''}`
      : `You are about to delete & deactivate ${activeSelected.length} selected user(s). This action can be undone later if "Include Deleted" is checked.`;
    const yesButtonText =
      deletedSelected.length > 0 && activeSelected.length > 0
        ? 'Apply Changes'
        : isRestore
          ? 'Restore'
          : 'Delete';
    const yesBtnCss = isRestore ? 'btn-primary' : 'btn-danger';

    const confirmed = await this.confirmationService.confirm({
      header,
      bodyHeader,
      bodySubtitle,
      yesButtonText,
      yesBtnCss,
      noBtnCss: "btn-secondary",
      noButtonText: "Cancel",
      width: "500px",
    });

    if (confirmed) {
      this.adminService.deleteUsers(selectedIds).subscribe({
        next: res => {
          this.fetchUsers();
        },
        error: err => {
          console.error("Error processing users", err);
        }
      });
    }
  }

  view(user: IUser) {
    this.selectedUser.set(user);
    this.isView.set(true);
  }

  closeView() {
    this.selectedUser.set(null);
    this.isView.set(false);
  }

  previousPage() {
    if (this.currentPage() > 1) {
      this.currentPage.update(v => v - 1);
      this.updatePagination();
      this.fetchUsers();
    }
  }

  nextPage() {
    if (this.currentPage() < this.totalPages) {
      this.currentPage.update(v => v + 1);
      this.updatePagination();
      this.fetchUsers();
    }
  }

  updatePagination() {
    this.totalPages = Math.ceil(this.totalCount() / this.pageSize());
    this.startIndex = (this.currentPage() - 1) * this.pageSize() + 1;
    this.endIndex = Math.min(
      this.currentPage() * this.pageSize(),
      this.totalCount()
    );

    this.updateSelectAllState();
  }

  onSearch(value: string | null) {
    this.currentPage.set(1);
    this.searchTerm.set(value);
    this.fetchUsers();
  }

  updateSelectAllState() {
    const allUsers = this.users();
    const selectedCount = allUsers.filter(user => user.selected).length;
    this.allSelected.set(
      selectedCount === allUsers.length && allUsers.length > 0
    );
    this.hasSelection.set(selectedCount > 0);
    this.hasDeletedSelection.set(allUsers.some(u => u.selected && u.deletedAt));
  }

  getProfileImage(u: IUser): string | undefined {
    const photos = u.profilePhotos;
    if (!photos || photos.length === 0) return undefined;
    const primary = photos.find(p => p.isPrimary);
    return (primary ?? photos[0])?.signedUrl || undefined;
  }

  getDisplayName(u: IUser): string {
    const first = u.firstName?.trim();
    const last = u.lastName?.trim();
    if (!first && !last) return '';
    return `${first ?? ''} ${last ?? ''}`.trim();
  }

  getInitials(u: IUser): string {
    const first = u.firstName?.trim()?.[0] ?? '';
    const last = u.lastName?.trim()?.[0] ?? '';
    const initials = (first + last).toUpperCase();
    return initials || 'N/A';
  }

  openPreview(user: IUser): void {
    const photos = user.profilePhotos || [];
    this.previewPhotos.set(photos);
    const primaryIndex = photos.findIndex(p => p.isPrimary);
    this.previewPhotoIndex.set(primaryIndex >= 0 ? primaryIndex : 0);

    // Preload all photos in the background for instant navigation
    photos.forEach(photo => {
      if (photo.signedUrl) {
        const img = new Image();
        img.src = photo.signedUrl;
      }
    });
  }

  closePreview(): void {
    this.previewPhotos.set([]);
    this.previewPhotoIndex.set(0);
  }

  prevPreviewPhoto(event: MouseEvent): void {
    event.stopPropagation();
    const photos = this.previewPhotos();
    if (photos.length <= 1) return;
    this.previewPhotoIndex.update(idx => (idx - 1 + photos.length) % photos.length);
  }

  nextPreviewPhoto(event: MouseEvent): void {
    event.stopPropagation();
    const photos = this.previewPhotos();
    if (photos.length <= 1) return;
    this.previewPhotoIndex.update(idx => (idx + 1) % photos.length);
  }

  openImageInNewTab(url: string): void {
    window.open(url, "_blank");
  }
}
